import loadable from '@loadable/component';
import ErrorBoundary from 'components/ErrorBoundary';
import React, {
  ComponentType,
  ReactElement,
  ReactNode,
  useEffect,
} from 'react';
import { Route, RouteComponentProps, RouteProps } from 'react-router-dom';
import { isCordova } from 'utils/platform';
import useBreakPoint from '~/hooks/useBreakPoint';
import { toggleMessenger } from '~/trackers/tools/zendesk/handler';

const LoadableNavbar = loadable(
  () =>
    import(
      /* webpackChunkName: "navbar" */ '~/reusable-components/navigation/Navbar'
    ),
  {},
  true,
);
const LoadableTabBar = loadable(
  () =>
    import(
      /* webpackChunkName: "tabbar" */ '~/reusable-components/navigation/TabBar'
    ),
  {},
  true,
);
const LoadableFooter = loadable(
  () => import(/* webpackChunkName: "footer" */ 'modules/common/Footer'),
  {},
  true,
);

export type NavigationableLayoutProps = RouteProps & {
  exact?: boolean;
  overlay?: ReactNode;
  transparentNavbar?: boolean;
  nonFixedNavbar?: boolean;
  component: ComponentType;
  onTopOf?: ComponentType;
  showSearchInNavbar?: boolean;
  showFullSearchInNavbar?: boolean;
  hideFooter?: boolean;
  showBottomNavigation?: boolean;
  subNavbar?: ReactElement;
  subNavbarMobile?: ReactElement;
  bottomBar?: ReactElement;
  noNavbar?: boolean;
  showZendesk?: boolean;
};

const NavigationableLayout = ({
  component: SubComponent,
  onTopOf: UnderlyingPage,
  overlay,
  showSearchInNavbar = true,
  showFullSearchInNavbar = false,
  hideFooter = false,
  noNavbar,
  transparentNavbar = false,
  nonFixedNavbar,
  showBottomNavigation,
  subNavbar,
  subNavbarMobile,
  bottomBar,
  showZendesk,
  path,
  location,
  exact,
}: NavigationableLayoutProps) => {
  const breakpoint = useBreakPoint();
  const isMobile = breakpoint === 'xs';

  useEffect(() => {
    const shouldShowZendesk = showZendesk === true || showZendesk === undefined;
    toggleMessenger(shouldShowZendesk && !isMobile);
  }, [showZendesk, isMobile]);

  const renderContent = (matchProps: RouteComponentProps<any>) => (
    <>
      {!noNavbar && (
        <LoadableNavbar
          fixed={nonFixedNavbar ? 'none' : 'all'}
          variant={transparentNavbar ? 'transparent' : 'default'}
          showSearch={showSearchInNavbar}
          showFullSearch={showFullSearchInNavbar}
          subNavbar={subNavbar}
          subNavbarMobile={subNavbarMobile}
        />
      )}
      <ErrorBoundary>
        {UnderlyingPage && <UnderlyingPage />}
        <SubComponent {...matchProps} />
      </ErrorBoundary>
      {overlay}
      {hideFooter ? null : <LoadableFooter />}
      {showBottomNavigation && isCordova() && (
        <LoadableTabBar bottomBar={bottomBar} />
      )}
    </>
  );

  return (
    <Route
      exact={exact}
      location={location}
      path={path}
      render={renderContent}
    />
  );
};

export default NavigationableLayout;
